import type {
  AlternateRouteDefinition,
  MapDefinition,
  RoadSectionDefinition,
  RoadStyle,
} from './types.js';
import { clamp, trackProgressAtIndex } from './track.js';

export interface RouteSurfaceProfile {
  grip: number;
  speed: number;
  roughness: number;
}

export interface RoadVisualProfile {
  laneOffsets: readonly number[];
  dashLength: number;
  dashWidth: number;
  colour: string;
  edgeColour: string;
}

const DEFAULT_ROAD_PROFILE: RoadVisualProfile = {
  laneOffsets: [0],
  dashLength: 3.2,
  dashWidth: 0.16,
  colour: '#f4f7f8',
  edgeColour: '#dce6e8',
};

/** Returns the authored lap act at an arc-length progress value. */
export function roadSectionAtProgress(
  map: MapDefinition,
  progressValue: number,
): RoadSectionDefinition | undefined {
  const sections = map.features.roadSections ?? [];
  if (sections.length === 0) return undefined;
  const progress = mod(progressValue, 1);
  for (const section of sections) {
    const from = trackProgressAtIndex(map, section.fromTrackIndex);
    const to = trackProgressAtIndex(map, section.toTrackIndex);
    if (cyclicProgressContains(progress, from, to)) return section;
  }
  return sections[0];
}

export function roadVisualProfile(
  styleValue: RoadStyle | undefined,
  accent?: string,
): RoadVisualProfile {
  const style = styleValue ?? 'mountain';
  const profile: RoadVisualProfile = (() => {
    switch (style) {
      case 'boulevard': return {
        laneOffsets: [-0.18, 0.18], dashLength: 4.8, dashWidth: 0.13,
        colour: '#f4f7ff', edgeColour: '#8de7ff',
      };
      case 'expressway': return {
        laneOffsets: [-0.17, 0.17], dashLength: 5.3, dashWidth: 0.14,
        colour: '#f6f8ff', edgeColour: '#a4c8ff',
      };
      case 'urban': return {
        laneOffsets: [0], dashLength: 2.6, dashWidth: 0.2,
        colour: '#f5f6ff', edgeColour: '#ff59dc',
      };
      case 'industrial': return {
        laneOffsets: [0], dashLength: 3.1, dashWidth: 0.2,
        colour: '#ffd36a', edgeColour: '#ff9f43',
      };
      case 'village': return {
        laneOffsets: [0], dashLength: 2.7, dashWidth: 0.17,
        colour: '#ffe1a0', edgeColour: '#ffbf65',
      };
      case 'shore': return {
        laneOffsets: [0], dashLength: 3.8, dashWidth: 0.16,
        colour: '#e5fbff', edgeColour: '#61e3ff',
      };
      case 'bridge': return {
        laneOffsets: [0], dashLength: 5.4, dashWidth: 0.2,
        colour: '#f8fbff', edgeColour: '#6fe8ff',
      };
      case 'canyon': return {
        laneOffsets: [0], dashLength: 3.25, dashWidth: 0.18,
        colour: '#fff0c4', edgeColour: '#ff8054',
      };
      case 'quarry': return {
        laneOffsets: [0], dashLength: 2.8, dashWidth: 0.2,
        colour: '#ffd06e', edgeColour: '#ffae32',
      };
      case 'desert': return {
        laneOffsets: [0], dashLength: 3.7, dashWidth: 0.17,
        colour: '#fff1c9', edgeColour: '#ffb14e',
      };
      case 'jungle': return {
        laneOffsets: [0], dashLength: 3.1, dashWidth: 0.18,
        colour: '#f6f0c8', edgeColour: '#7dff9a',
      };
      case 'lunar': return {
        laneOffsets: [-0.2, 0.2], dashLength: 6.2, dashWidth: 0.12,
        colour: '#eef4ff', edgeColour: '#7fc8ff',
      };
      case 'mountain': return DEFAULT_ROAD_PROFILE;
    }
  })();
  return accent ? { ...profile, edgeColour: accent } : profile;
}

/** Handling trade-offs remain deliberately mild: branches should create a
 * choice, not bring back the overly slippery steering players rejected. */
export function routeSurfaceProfile(route: AlternateRouteDefinition | undefined): RouteSurfaceProfile {
  switch (route?.surface ?? 'asphalt') {
    case 'gravel': return { grip: 0.9, speed: 0.89, roughness: 0.96 };
    case 'service': return { grip: 0.95, speed: 0.94, roughness: 0.82 };
    case 'asphalt': return { grip: 1, speed: 1, roughness: 0.68 };
  }
}

export function routeForId(map: MapDefinition, id: string | undefined): AlternateRouteDefinition | undefined {
  if (!id) return undefined;
  return map.features.alternateRoutes?.find((route) => route.id === id);
}

export function routeReward(route: AlternateRouteDefinition): number {
  return clamp(route.reward ?? (route.kind === 'loop' ? 10 : 8), 0, 25);
}

/** Authored reward is the readable baseline shown in map data. The actual
 * payout also values a route's risk and compromised surface so the fastest,
 * safest main line is not automatically the most rewarding choice. */
export function routeChallengeMultiplier(route: AlternateRouteDefinition): number {
  const risk = route.risk === 'extreme' ? 1.42 : route.risk === 'technical' ? 1.16 : 0.9;
  const surface = route.surface === 'gravel' ? 1.18 : route.surface === 'service' ? 1.08 : 1;
  const spectacle = route.kind === 'loop' ? 1.16 : 1;
  return clamp(risk * surface * spectacle, 0.75, 1.85);
}

export function routePayout(
  route: AlternateRouteDefinition,
  completionSpeed: number,
  clean: boolean,
): number {
  const speedQuality = clamp((completionSpeed - 7) / 24, 0.62, 1.18);
  const cleanQuality = clean ? 1.1 : 0.82;
  return clamp(routeReward(route) * routeChallengeMultiplier(route) * speedQuality * cleanQuality, 3, 38);
}

function cyclicProgressContains(progress: number, from: number, to: number): boolean {
  if (from < to) return progress >= from && progress < to;
  return progress >= from || progress < to;
}

function mod(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}
